import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    categoryList = "categoryList",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.categoryList]: string;
        [fields.customCSS]: string;
    };
    extendedStyles?: LinkListStyles;
}

interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-family: "Roboto-Bold";
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
        `,
    },
    title: {
        css: css`
            background: none;
            border: 2px solid ${getColor("secondary")};
            color: ${getColor("secondary")};
            padding: 10px;
            text-align: center;
            font-size: 20.8px;
            font-family: "Roboto-Regular";
        `,
    },
    link: {
        icon: {
            iconProps: {
                src: ArrowRight,
                size: 16,
                css: css`
                    margin-right: 4px;
                    color: #6d6e71 !important;
                `,
            },
        },
        css: css`
            padding: 0.4rem 2rem 0.4rem 0;
            transition: all 0.5s ease-in-out;
            ${TypographyStyle} {
                color: #6d6e71 !important;
                font-family: "Roboto-Regular";
                font-size: 14.4px !important;
            }

            &:hover {
                padding: 0.5rem;
                ${TypographyStyle} {
                    text-decoration: underline;
                }
            }
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const SEBlogDetailsAllCategories: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    if (fields.categoryList.trim() === "") {
        return null;
    }

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper">
                <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                    <Typography className="list-title" as="h4" {...styles.title}>
                        {translate("All Categories")}
                    </Typography>
                </StyledWrapper>
                <UnorderedList
                    className="links-wrapper"
                    css={css`
                        padding: 0px;
                    `}
                >
                    {fields.categoryList.split(",").map((segment, index) => (
                        <ListItem
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            css={css`
                                list-style: none;
                                padding-top: 0.25rem;
                            `}
                        >
                            {segment && (
                                <Link {...styles.link} className="list-link" href={`/blog?category=${segment.trim()}`}>
                                    {segment}
                                </Link>
                            )}
                        </ListItem>
                    ))}
                </UnorderedList>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: SEBlogDetailsAllCategories,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.categoryList,
                displayName: "Category List",
                editorTemplate: "TextField",
                defaultValue:
                    "100th Anniversary,Associate Spotlight,Automation Express,Cables,Controls,Drives,Enclosures,Featured Products,Hot Products,IIoT,Industrial News,Lighting,Safety Products,Our Experts,Sensors,Standard News,Wireless,Uncategorized",
                sortOrder: 1,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
